import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Leaf, AlertCircle, Baby, User, Heart, UserCheck, Users, Sparkles, FlaskConical, MapPin, BookOpen, Beaker, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { ProNavigation } from "@/components/ProNavigation";
import { ageCategories, generalGuidelines, plantsToAvoid, recommendedForms } from "@/data/ageGuidelines";
import regardDeNatureLogo from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";

interface MedicinalPlant {
  id: string;
  latin_name: string;
  common_names: string[];
  family: string;
  description: string;
  habitat_culture: string;
  main_constituents: string[];
  main_effects: string[];
  traditional_uses: string;
  current_research: string;
  parts_used: string[];
  preparations: string;
  precautions: string;
  contraindications: string[];
  image_url: string | null;
  keywords: string[];
}

const getAgeCategoryIcon = (categoryId: string) => {
  switch (categoryId) {
    case 'infant': return <Baby className="w-5 h-5" />;
    case 'child': return <Baby className="w-5 h-5" />;
    case 'preteen': return <User className="w-5 h-5" />;
    case 'pregnant': return <Heart className="w-5 h-5" />;
    case 'breastfeeding': return <Heart className="w-5 h-5" />;
    case 'elderly': return <UserCheck className="w-5 h-5" />;
    default: return <User className="w-5 h-5" />;
  }
};

const MedicinalPlantsLibrary = () => {
  const navigate = useNavigate();
  const [plants, setPlants] = useState<MedicinalPlant[]>([]);
  const [filteredPlants, setFilteredPlants] = useState<MedicinalPlant[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("plants");

  useEffect(() => {
    loadPlants();
  }, []);

  useEffect(() => {
    filterPlants();
  }, [searchQuery, plants]);

  const loadPlants = async () => {
    try {
      const { data, error } = await supabase.from("medicinal_plants").select("*").order("latin_name");

      if (error) throw error;
      setPlants(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des plantes:", error);
      toast.error("Impossible de charger les plantes médicinales");
    } finally {
      setLoading(false);
    }
  };

  const filterPlants = () => {
    if (!searchQuery.trim()) {
      setFilteredPlants(plants);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = plants.filter((plant) => {
      return (
        plant.latin_name.toLowerCase().includes(query) ||
        plant.common_names.some((name) => name.toLowerCase().includes(query)) ||
        plant.family?.toLowerCase().includes(query) ||
        plant.keywords?.some((keyword) => keyword.toLowerCase().includes(query)) ||
        plant.main_effects?.some((effect) => effect.toLowerCase().includes(query))
      );
    });
    setFilteredPlants(filtered);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <ProNavigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Page Header - Style REGARD DE NATURE */}
        <div className="mb-10 text-center">
          <div className="flex flex-col items-center gap-4 mb-6">
            <img 
              src={regardDeNatureLogo} 
              alt={BRAND.name.full} 
              className="w-20 h-20 sm:w-24 sm:h-24 drop-shadow-md"
            />
            <div className="flex items-center gap-3">
              <Leaf className="w-8 h-8 sm:w-10 sm:h-10 text-secondary" />
              <h1 className="text-2xl sm:text-4xl font-cinzel font-bold">
                <span className="text-secondary">Plantes</span>
                <span className="text-primary"> Médicinales</span>
              </h1>
            </div>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto font-montserrat">
            Bibliothèque complète des plantes médicinales et de leurs vertus thérapeutiques.
          </p>
          <div className="mt-4 flex justify-center">
            <Badge variant="outline" className="bg-secondary/10 text-secondary border-secondary/30 font-montserrat">
              {plants.length} plantes référencées
            </Badge>
          </div>
        </div>

        {/* Tabs for Plants and Age Guidelines */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1.5 bg-card border border-border/50 shadow-sm">
            <TabsTrigger 
              value="plants" 
              className="text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Leaf className="w-4 h-4 mr-1.5" />
              Plantes
            </TabsTrigger>
            <TabsTrigger 
              value="ages" 
              className="text-xs sm:text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Users className="w-4 h-4 mr-1.5" />
              Âges de la vie
            </TabsTrigger>
          </TabsList>

          {/* Plants Tab */}
          <TabsContent value="plants" className="mt-6">
            {/* Search Bar */}
            <Card className="mb-8 shadow-card border-border/50 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-secondary/5 border-b border-border/30">
                <CardTitle className="flex items-center gap-2 text-primary font-cinzel">
                  <Search className="w-5 h-5 text-secondary" />
                  Rechercher une plante
                </CardTitle>
                <CardDescription className="font-montserrat">
                  Recherchez par nom latin, nom commun, famille, effets ou mots-clés
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Ex: millefeuille, digestion, anti-inflammatoire..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 text-base border-border/60 shadow-sm focus:ring-2 focus:ring-secondary/20"
                  />
                </div>
                {searchQuery && (
                  <p className="mt-3 text-sm text-muted-foreground font-montserrat">
                    <span className="font-semibold text-secondary">{filteredPlants.length}</span> plante(s) trouvée(s)
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Plants List */}
            {loading ? (
              <div className="text-center py-16">
                <Leaf className="w-12 h-12 mx-auto mb-4 text-secondary animate-pulse" />
                <p className="text-muted-foreground font-montserrat">Chargement des plantes...</p>
              </div>
            ) : filteredPlants.length === 0 ? (
              <Card className="p-12 text-center border-border/50 shadow-card">
                <Leaf className="w-16 h-16 mx-auto mb-4 text-accent" />
                <p className="text-lg text-muted-foreground font-montserrat">
                  {searchQuery
                    ? "Aucune plante ne correspond à votre recherche"
                    : "Aucune plante disponible dans la bibliothèque"}
                </p>
              </Card>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-1">
                {filteredPlants.map((plant) => (
                  <Card 
                    key={plant.id} 
                    className="shadow-card hover:shadow-lg transition-all duration-300 border-border/50 overflow-hidden group"
                  >
                    <CardHeader className="bg-gradient-to-r from-primary/5 via-secondary/5 to-accent/5 border-b border-border/30">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-secondary/15 flex items-center justify-center shrink-0">
                              <Leaf className="w-5 h-5 text-secondary" />
                            </div>
                            <CardTitle className="text-xl sm:text-2xl text-primary font-cinzel group-hover:text-secondary transition-colors">
                              {plant.latin_name}
                            </CardTitle>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {plant.common_names.map((name, idx) => (
                              <Badge 
                                key={idx} 
                                className="bg-secondary/15 text-secondary border-secondary/30 hover:bg-secondary/25 font-montserrat"
                              >
                                {name}
                              </Badge>
                            ))}
                          </div>
                          {plant.family && (
                            <p className="text-sm text-muted-foreground italic font-montserrat flex items-center gap-1.5">
                              <Sparkles className="w-3.5 h-3.5 text-accent" />
                              Famille: {plant.family}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <p className="text-base mb-5 leading-relaxed text-foreground/90 font-montserrat">{plant.description}</p>

                      <Accordion type="single" collapsible className="w-full space-y-2">
                        <AccordionItem value="effects" className="border border-border/40 rounded-lg overflow-hidden">
                          <AccordionTrigger className="px-4 py-3 text-base font-semibold hover:bg-primary/5 font-montserrat">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-secondary" />
                              Principaux effets
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4 pt-2">
                            <div className="flex flex-wrap gap-2">
                              {plant.main_effects?.map((effect, idx) => (
                                <Badge key={idx} className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 font-montserrat">
                                  {effect}
                                </Badge>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>

                        {plant.main_constituents?.length > 0 && (
                          <AccordionItem value="constituents" className="border border-border/40 rounded-lg overflow-hidden">
                            <AccordionTrigger className="px-4 py-3 text-base font-semibold hover:bg-primary/5 font-montserrat">
                              <div className="flex items-center gap-2">
                                <FlaskConical className="w-4 h-4 text-accent" />
                                Principaux constituants
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 pt-2">
                              <ul className="list-none space-y-2">
                                {plant.main_constituents.map((constituent, idx) => (
                                  <li key={idx} className="text-sm flex items-start gap-2 font-montserrat">
                                    <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                                    {constituent}
                                  </li>
                                ))}
                              </ul>
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {plant.parts_used?.length > 0 && (
                          <AccordionItem value="parts" className="border border-border/40 rounded-lg overflow-hidden">
                            <AccordionTrigger className="px-4 py-3 text-base font-semibold hover:bg-primary/5 font-montserrat">
                              <div className="flex items-center gap-2">
                                <Leaf className="w-4 h-4 text-primary" />
                                Parties utilisées
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 pt-2">
                              <div className="flex flex-wrap gap-2">
                                {plant.parts_used.map((part, idx) => (
                                  <Badge key={idx} variant="outline" className="border-accent/40 text-accent font-montserrat">{part}</Badge>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {plant.habitat_culture && (
                          <AccordionItem value="habitat" className="border border-border/40 rounded-lg overflow-hidden">
                            <AccordionTrigger className="px-4 py-3 text-base font-semibold hover:bg-primary/5 font-montserrat">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-secondary" />
                                Habitat et culture
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 pt-2">
                              <p className="text-sm leading-relaxed font-montserrat">{plant.habitat_culture}</p>
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {plant.traditional_uses && (
                          <AccordionItem value="traditional" className="border border-border/40 rounded-lg overflow-hidden">
                            <AccordionTrigger className="px-4 py-3 text-base font-semibold hover:bg-primary/5 font-montserrat">
                              <div className="flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-primary" />
                                Usages traditionnels
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 pt-2">
                              <p className="text-sm leading-relaxed font-montserrat">{plant.traditional_uses}</p>
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {plant.current_research && (
                          <AccordionItem value="research" className="border border-border/40 rounded-lg overflow-hidden">
                            <AccordionTrigger className="px-4 py-3 text-base font-semibold hover:bg-primary/5 font-montserrat">
                              <div className="flex items-center gap-2">
                                <Beaker className="w-4 h-4 text-accent" />
                                Recherches en cours
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 pt-2">
                              <p className="text-sm leading-relaxed font-montserrat">{plant.current_research}</p>
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {plant.preparations && (
                          <AccordionItem value="preparations" className="border border-border/40 rounded-lg overflow-hidden">
                            <AccordionTrigger className="px-4 py-3 text-base font-semibold hover:bg-primary/5 font-montserrat">
                              <div className="flex items-center gap-2">
                                <FlaskConical className="w-4 h-4 text-secondary" />
                                Préparations et usages
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 pt-2">
                              <p className="text-sm leading-relaxed font-montserrat">{plant.preparations}</p>
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {(plant.precautions || plant.contraindications?.length > 0) && (
                          <AccordionItem value="warnings" className="border border-destructive/30 rounded-lg overflow-hidden bg-destructive/5">
                            <AccordionTrigger className="px-4 py-3 text-base font-semibold text-destructive hover:bg-destructive/10 font-montserrat">
                              <div className="flex items-center gap-2">
                                <ShieldAlert className="w-4 h-4" />
                                Précautions et contre-indications
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 pt-2">
                              <div className="bg-destructive/10 p-4 rounded-lg border border-destructive/20">
                                {plant.precautions && <p className="text-sm mb-3 leading-relaxed font-montserrat">{plant.precautions}</p>}
                                {plant.contraindications?.length > 0 && (
                                  <div>
                                    <p className="font-semibold text-sm mb-2 text-destructive font-montserrat">Contre-indications:</p>
                                    <ul className="list-none space-y-1.5">
                                      {plant.contraindications.map((contraindication, idx) => (
                                        <li key={idx} className="text-sm flex items-start gap-2 font-montserrat">
                                          <AlertCircle className="w-3.5 h-3.5 text-destructive mt-0.5 shrink-0" />
                                          {contraindication}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        )}
                      </Accordion>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Age Guidelines Tab */}
          <TabsContent value="ages" className="mt-6">
            {/* Introduction Card */}
            <Card className="mb-8 bg-gradient-to-r from-primary/5 via-secondary/5 to-accent/5 border-border/50 shadow-card overflow-hidden">
              <CardHeader className="border-b border-border/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary/15 flex items-center justify-center">
                    <Users className="w-5 h-5 text-secondary" />
                  </div>
                  <CardTitle className="text-xl text-primary font-cinzel">{generalGuidelines.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-4 font-montserrat">{generalGuidelines.introduction}</p>
                <div className="bg-card/80 p-4 rounded-lg border border-border/40">
                  <p className="font-semibold text-sm mb-3 text-primary font-montserrat">À retenir :</p>
                  <ul className="space-y-2">
                    {generalGuidelines.keyPoints.map((point, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2 font-montserrat">
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Age Categories Grid */}
            <div className="grid gap-6 md:grid-cols-2">
              {ageCategories.map((category) => (
                <Card key={category.id} className="shadow-card hover:shadow-lg transition-all duration-300 border-border/50 overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-primary/5 to-secondary/5 border-b border-border/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-secondary/15 rounded-full text-secondary">
                        {getAgeCategoryIcon(category.id)}
                      </div>
                      <div>
                        <CardTitle className="text-lg font-cinzel text-primary">{category.label}</CardTitle>
                        <CardDescription className="font-montserrat">{category.ageRange}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-5 space-y-4">
                    <p className="text-sm text-muted-foreground font-montserrat">{category.description}</p>

                    {/* Allowed Forms */}
                    <div>
                      <p className="font-semibold text-sm text-primary mb-2 flex items-center gap-1.5 font-montserrat">
                        <span className="w-4 h-4 rounded-full bg-primary/15 flex items-center justify-center text-xs">✓</span>
                        Formes autorisées :
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {category.allowedForms.map((form, idx) => (
                          <Badge key={idx} variant="outline" className="bg-primary/10 text-primary border-primary/20 text-xs font-montserrat">
                            {form}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Forbidden Forms */}
                    {category.forbiddenForms.length > 0 && (
                      <div>
                        <p className="font-semibold text-sm text-destructive mb-2 flex items-center gap-1.5 font-montserrat">
                          <span className="w-4 h-4 rounded-full bg-destructive/15 flex items-center justify-center text-xs">✗</span>
                          Formes à éviter :
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {category.forbiddenForms.map((form, idx) => (
                            <Badge key={idx} variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 text-xs font-montserrat">
                              {form}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Safe Plants */}
                    {category.safePlants.length > 0 && (
                      <div>
                        <p className="font-semibold text-sm text-accent mb-2 flex items-center gap-1.5 font-montserrat">
                          <Leaf className="w-4 h-4" />
                          Plantes douces possibles :
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {category.safePlants.map((plant, idx) => (
                            <Badge key={idx} className="bg-accent/15 text-accent border-accent/20 text-xs font-montserrat">
                              {plant}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Plants to Avoid */}
                    {plantsToAvoid[category.id]?.length > 0 && (
                      <div>
                        <p className="font-semibold text-sm text-secondary mb-2 flex items-center gap-1.5 font-montserrat">
                          <AlertCircle className="w-4 h-4" />
                          Plantes à éviter :
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {plantsToAvoid[category.id].map((plant, idx) => (
                            <Badge key={idx} variant="outline" className="bg-secondary/10 text-secondary border-secondary/20 text-xs font-montserrat">
                              {plant}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Warnings */}
                    {category.warnings.length > 0 && (
                      <div className="bg-secondary/10 p-3 rounded-lg border border-secondary/20">
                        <p className="font-semibold text-sm text-secondary mb-2 flex items-center gap-1.5 font-montserrat">
                          <AlertCircle className="w-4 h-4" />
                          Précautions :
                        </p>
                        <ul className="space-y-1.5">
                          {category.warnings.map((warning, idx) => (
                            <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2 font-montserrat">
                              <span className="w-1 h-1 rounded-full bg-secondary mt-1.5 shrink-0" />
                              {warning}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Dosage Notes */}
                    {category.dosageNotes.length > 0 && (
                      <div className="bg-primary/5 p-3 rounded-lg border border-primary/20">
                        <p className="font-semibold text-sm text-primary mb-2 flex items-center gap-1.5 font-montserrat">
                          <BookOpen className="w-4 h-4" />
                          Posologie :
                        </p>
                        <ul className="space-y-1.5">
                          {category.dosageNotes.map((note, idx) => (
                            <li key={idx} className="text-xs text-muted-foreground flex items-start gap-2 font-montserrat">
                              <span className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
                              {note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer Disclaimer */}
        <Card className="mt-10 bg-secondary/10 border-secondary/30 shadow-card overflow-hidden">
          <CardContent className="py-5">
            <p className="text-sm text-foreground/80 flex items-start gap-3 font-montserrat">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-secondary" />
              <span>
                <strong className="text-primary">Avertissement :</strong> Les informations présentées dans cette bibliothèque sont fournies à
                titre informatif uniquement et ne remplacent pas un avis médical professionnel. Consultez toujours un
                professionnel de santé qualifié avant d'utiliser des plantes médicinales, en particulier en cas de
                grossesse, d'allaitement, ou de traitement médical en cours.
              </span>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MedicinalPlantsLibrary;
